import CONFIG from '../config/config';
import { ItemVariant } from './CollectionItemVariantsAPI';
import { DressItem } from './DressesAPI';
import { instanceWithV2 as APIClient, serializeParamsForSpringBoot } from './SimpleAPIClient';

export type CollectionViewCountry = {
  countryCode: string;
  count: number;
  percent: number;
};

export type Collection = {
  id?: number;
  name: string;
  shortName: string;
  type: string; // deprecated
  types: string[];
  views: number;
  viewCountries?: CollectionViewCountry[];
  description: string;
  featured: boolean; // deprecated
  public: boolean;
  lastUpdateTime: string | null;
  createdAt?: string | null;
  items: Item[];
  shareableByLink: boolean;
  pricesArePublic: boolean;
  acceptsB2BOrders?: boolean;
  showBookAppointment?: boolean;
  uuid?: string;
  slug?: string;
};

export type Item = {
  id?: number;
  name: string;
  featuredPictureUrl: string; // deprecated
  type: string; // deprecated
  placeOrder: number;
  media?: string[];
  files?: File[]
  price?: number;
  currency?: string;
  cost?: number;
  costCurrency?: string;
  description?: string;
  plan?: DressItem;
  collectionName?: string;
  collection?: number;
  variations?: ItemVariant[];
  isExternal?: boolean;
};

export type CollectionVisibilityFilter = 'public' | 'shareableByLink' | 'private';

export type CollectionSearchFilters = {
  page?: number;
  size?: number;
  visibility?: CollectionVisibilityFilter[];
  pricesArePublic?: boolean;
};

export type CollectionsPageResponse = {
  content: Collection[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};

const CollectionsAPI = {
  getCollections: (type?: string) => APIClient(CONFIG.API_ENDPOINT).get<Collection[]>(`/v1/collections${type ? `?type=${type}` : ''}`),
  searchCollections: ({ page = 0, size = 10, visibility, pricesArePublic }: CollectionSearchFilters) => {
    const params: Record<string, unknown> = { page, size };
    if (visibility && visibility.length > 0) {
      params.visibility = visibility;
    }
    if (pricesArePublic !== undefined) {
      params.pricesArePublic = pricesArePublic;
    }
    return APIClient(CONFIG.API_ENDPOINT).get<CollectionsPageResponse>(`/v1/collections`, {
      params,
      paramsSerializer: serializeParamsForSpringBoot,
    });
  },
  add: (collection: Collection) => APIClient(CONFIG.API_ENDPOINT).post<Collection>(`/v1/collections`, collection),
  checkSlugAvailability: (alias: string, signal?: AbortSignal) =>
    APIClient(CONFIG.API_ENDPOINT).post<string>(`/v1/collections/slug/check`, { alias }, { signal }),
  invite: (id: number, emails: string[]) => APIClient(CONFIG.API_ENDPOINT).put<any>(`/v1/collections/${id}/invite`, { id, emails }),
  duplicate: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<Collection>(`/v1/collections/${id}/duplicates`),
  remove: (id: number) => APIClient(CONFIG.API_ENDPOINT).delete<Collection>(`/v1/collections/${id}`),
  update: (id: number, collection: Collection) => APIClient(CONFIG.API_ENDPOINT).put<Collection>(`/v1/collections/${id}`, collection),
  searchItems: (searchText: string) => APIClient(CONFIG.API_ENDPOINT).get<Item[]>(`/v1/collections/items?searchText=${searchText}`),
  getItem: (id: number, itemId: number) => APIClient(CONFIG.API_ENDPOINT).get<Item>(`/v1/collections/${id}/${itemId}`),
  addItem: (id: number, item: Item) => APIClient(CONFIG.API_ENDPOINT).post<Item>(`/v1/collections/${id}`, item),
  updateItem: (id: number, itemId: number, item: Item) => APIClient(CONFIG.API_ENDPOINT).patch<Item>(`/v1/collections/${id}/${itemId}`, item),
  addImage: (id: number, itemId: number, url: string) => APIClient(CONFIG.API_ENDPOINT).patch<Item>(`/v1/collections/${id}/${itemId}/files/images`,
  { collection_id: id, id: itemId, featuredPictureUrl: url }
  ),
  removeItem: (id: number, itemId: number) => APIClient(CONFIG.API_ENDPOINT).delete<Item>(`/v1/collections/${id}/${itemId}`),
};

export default CollectionsAPI;
